export const getUpdatedCampaignConfiguration = (
  campaignsConfiguration,
  campaignGroups
) => {
  let temporaryCampaignsConfig = JSON.parse(JSON.stringify(campaignsConfiguration));
  const updatedConfigs = [];

  if (campaignGroups?.length && temporaryCampaignsConfig.length) {
    campaignGroups.forEach((group) => {
      const updatedGroup = {
        key: group.key,
        image: group.imageURL,
        name: group.name,
        campaigns: [],
        value: null,
      };

      if (group.campaigns?.length) {
        group.campaigns.forEach((campaignKey) => {
          const campaign = temporaryCampaignsConfig.find(
            (config) => config.key === campaignKey
          );
          if (campaign) {
            updatedGroup.campaigns.push(campaign);
            temporaryCampaignsConfig = temporaryCampaignsConfig.filter(
              (config) => config.key !== campaignKey
            );
          }
        });
      }

      updatedConfigs.push(updatedGroup);
    });
  }

  return temporaryCampaignsConfig.concat(updatedConfigs);
};

export const getCampaignKeyToTypesMap = (campaignConfiguration) => {
  const campaignKeyToTypesMap = {};
  campaignConfiguration.forEach((campaign) => {
    if (campaign.campaigns && campaign.campaigns.length > 0) {
      campaignKeyToTypesMap[campaign.key] = campaign.campaigns.map((c) => c.key);
      return;
    }
    campaignKeyToTypesMap[campaign.key] = [campaign.key];
  });
  return campaignKeyToTypesMap;
};

//sort activity rules based on the activityDefinition.rulesCOnfig.rules || activityDefinition.rules
export const sortActivityRules = (existingRules, activityDefinition) => {
  const rules = JSON.parse(JSON.stringify(existingRules));
  // Early return if activityDefinition is undefined
  if (!activityDefinition) {
    return rules;
  }

  // Get rule definitions
  const existingRuleDefs =
    activityDefinition.rulesConfiguration?.rules || activityDefinition.rules || [];
  // Create a slug-to-position mapping for faster lookups
  const slugPositionMap = {};
  // Extract all slugs in order, prioritizing the nested structure
  existingRuleDefs.forEach((ruleDef, index) => {
    // Use rule.slug if available, otherwise try direct slug
    const slug = (ruleDef.rule && ruleDef.rule.slug) || ruleDef.slug;
    if (slug) {
      slugPositionMap[slug] = index;
    }
  });
  // Sort rules based on the position map
  const sortedRules = [...rules].sort((a, b) => {
    const posA = slugPositionMap[a.ruleDefinitionSlug];
    const posB = slugPositionMap[b.ruleDefinitionSlug];
    // Sort by position, unknown rules go to the end
    return (
      (posA === undefined ? Infinity : posA) - (posB === undefined ? Infinity : posB)
    );
  });

  return sortedRules;
};

const RULE_SLUGS = {
  MATCH_PRODUCTS_KEYWORDS: "match-package-products-keywords",
  MATCH_PRODUCTS_KEYWORD_POINTS: "match-package-products-keyword-points-calculator",
  MATCH_RECEIPT_PRODUCTS: "match-receipt-products-keywords",
  MATCH_RECEIPT_PRODUCTS_POINTS: "match-receipt-products-keyword-points-calculator",
  SURVEY_ANSWER_MATCHING: "survey-answer-matching-rule",
  RETAILER_MATCHING: "retailer-matching-rule",
  STORE_GROUP_MATCHING: "store-group-matching-rule",
};

const OUTCOME_SLUGS = {
  POINTS_BASED_STREAKS: "points-based-streaks",
};

const IGNORED_RULE_SLUGS = [
  RULE_SLUGS.MATCH_PRODUCTS_KEYWORDS,
  RULE_SLUGS.MATCH_RECEIPT_PRODUCTS,
];
const CUSTOM_HANDLED_RULE_SLUGS = [
  RULE_SLUGS.MATCH_PRODUCTS_KEYWORD_POINTS,
  RULE_SLUGS.MATCH_RECEIPT_PRODUCTS_POINTS,
];
const ACTIVITIES_CONFIGURATION_OUTCOMES = [OUTCOME_SLUGS.POINTS_BASED_STREAKS];

const createMappedRule = ({ ruleDefinition, ruleData }) => ({
  ruleDefinitionSlug: ruleDefinition?.slug,
  ruleData,
});

export const transformSurveyRuleData = (rule) => {
  const ruleData = { ...rule.ruleData };
  const { survey } = ruleData.condition_display;

  ruleData.survey_configuration = {
    survey_id: survey.surveyId,
    survey_name: survey.surveyName,
    questions: survey.questions.map((q) => ({
      question_id: q.questionId,
      question_title: q.questionTitle,
      answer_options: q.answerOptions.map((opt) => ({
        option_id: opt.optionId,
        option_text: opt.optionText,
      })),
    })),
  };
  delete ruleData.condition_display;

  return {
    ruleDefinitionSlug: rule?.ruleDefinition?.slug,
    ruleData,
  };
};

export const isOutcomeRuleConfigured = (rule) => {
  const slug = rule?.ruleDefinition?.slug;
  const ruleData = rule?.ruleData || {};

  if (slug === RULE_SLUGS.SURVEY_ANSWER_MATCHING) {
    return Boolean(
      ruleData.condition_display?.survey || ruleData.survey_configuration?.survey_id
    );
  }

  if (slug === RULE_SLUGS.RETAILER_MATCHING) {
    return Array.isArray(ruleData.keywords) && ruleData.keywords.length > 0;
  }

  if (slug === RULE_SLUGS.STORE_GROUP_MATCHING) {
    return (
      !!ruleData.store_configuration &&
      Array.isArray(ruleData.keywords) &&
      ruleData.keywords.length > 0
    );
  }

  return true;
};

export const shouldSerializeOutcomeRule = isOutcomeRuleConfigured;

// Helper to check if a rule is a winning limit rule
const isWinningLimitRule = (rule) => {
  const slug = rule?.ruleDefinition?.slug || "";
  const name = rule?.ruleDefinition?.name || "";
  return (
    slug.includes("wins") ||
    slug.includes("winning") ||
    name.toLowerCase().includes("wins") ||
    name.toLowerCase().includes("winning")
  );
};

// Helper to extract timeframe from rule slug
const extractTimeframeFromSlug = (slug) => {
  if (slug.includes("hourly")) return "HOURLY";
  if (slug.includes("daily")) return "DAILY";
  if (slug.includes("weekly")) return "WEEKLY";
  if (slug.includes("monthly")) return "MONTHLY";
  if (slug.includes("yearly")) return "YEARLY";
  if (slug.includes("overall") || slug.includes("campaign")) return "OVERALL";
  return "OVERALL";
};

// Helper to extract campaignWinningLimit from rules
const extractCampaignWinningLimit = (rules = []) => {
  const winningRules = rules.filter(isWinningLimitRule);

  if (winningRules.length === 0) {
    return null;
  }

  const limits = winningRules.map((rule) => {
    const timeframe = extractTimeframeFromSlug(rule.ruleDefinition.slug);
    // Extract limit value from ruleData - could be in different fields
    const limit =
      rule.ruleData?.wins_hourly ||
      rule.ruleData?.wins_daily ||
      rule.ruleData?.wins_weekly ||
      rule.ruleData?.wins_monthly ||
      rule.ruleData?.wins_yearly ||
      rule.ruleData?.wins_overall ||
      rule.ruleData?.limit ||
      0;

    return {
      timeframe,
      limit,
    };
  });

  return {
    enabled: true,
    limits,
  };
};

const mapActivityRules = (rules = []) =>
  rules
    .filter((rule) => !IGNORED_RULE_SLUGS.includes(rule.ruleDefinition.slug))
    .map(({ ruleDefinition, ruleData }) => ({
      ruleDefinitionSlug: ruleDefinition.slug,
      ruleData,
    }));

const getCampaignReferenceSets = (campaignObject) => {
  const catalogRefs = campaignObject?.catalogRefs || [];
  const leaderboardIds = campaignObject?.leaderboards || [];
  const uniqueCatalogIds = new Set();
  const uniqueLeaderboardIds = new Set();

  if (catalogRefs?.length) {
    catalogRefs.forEach((ref) => {
      if (ref.catalogId) {
        uniqueCatalogIds.add(ref.catalogId);
      }
    });
  }

  if (leaderboardIds?.length) {
    leaderboardIds.forEach((id) => {
      uniqueLeaderboardIds.add(id);
    });
  }

  return {
    catalogRefs,
    uniqueCatalogIds,
    uniqueLeaderboardIds,
  };
};

export const updateCampaignReferencesForLeaderboardOutcome = (
  outcome,
  campaignObject,
  catalogRefs,
  uniqueCatalogIds,
  uniqueLeaderboardIds,
  addedCatalogIds
) => {
  if (outcome?.outcomeDefinition?.slug !== "award-leaderboard-points") {
    return;
  }

  if (outcome.outcomeData?.rewards && Array.isArray(outcome.outcomeData.rewards)) {
    outcome.outcomeData.rewards.forEach((reward) => {
      addedCatalogIds.add(reward.catalog_id);

      if (reward.catalog_id && !uniqueCatalogIds.has(reward.catalog_id)) {
        uniqueCatalogIds.add(reward.catalog_id);
        catalogRefs.push({
          catalogId: reward.catalog_id,
          attachedAt: new Date().toISOString(),
        });
      }
    });
  }

  uniqueLeaderboardIds.add(outcome?.outcomeData?.leaderboard_id);

  campaignObject.catalogRefs = catalogRefs.filter((ref) =>
    addedCatalogIds.has(ref.catalogId)
  );
  campaignObject.leaderboards = [...uniqueLeaderboardIds];
};

const createMappedOutcome = (outcome, params) => {
  const { outcomeDefinition, outcomeData, outcomeRules, id } = outcome;

  return {
    ...(params?.campaignId && id ? { id } : {}),
    outcomeDefinitionSlug: outcomeDefinition?.slug,
    outcomeData,
    outcomeRules: outcomeRules?.filter(shouldSerializeOutcomeRule).map((rule) => {
      if (
        rule?.ruleDefinition?.slug === "survey-answer-matching-rule" &&
        rule.ruleData?.condition_display?.survey
      ) {
        return transformSurveyRuleData(rule);
      }
      return createMappedRule(rule);
    }),
  };
};

const mapOutcomes = (outcomes = [], campaignObject, params) => {
  const { catalogRefs, uniqueCatalogIds, uniqueLeaderboardIds } =
    getCampaignReferenceSets(campaignObject);
  const addedCatalogIds = new Set();

  return outcomes.map((outcome) => {
    updateCampaignReferencesForLeaderboardOutcome(
      outcome,
      campaignObject,
      catalogRefs,
      uniqueCatalogIds,
      uniqueLeaderboardIds,
      addedCatalogIds
    );

    return createMappedOutcome(outcome, params);
  });
};

const addKeywordRuleStrategy = (activityPayload, ruleDefinitionSlug, rules) => {
  const keywordIds = [...new Set(rules.flatMap((rule) => rule.ruleData.ids))];

  activityPayload.activityRules.push({
    ruleDefinitionSlug,
    ruleData: { ids: keywordIds },
  });
};

const applyCustomRuleStrategies = (activityPayload) => {
  const ruleStrategies = {
    [RULE_SLUGS.MATCH_PRODUCTS_KEYWORD_POINTS]: (rules) => {
      addKeywordRuleStrategy(
        activityPayload,
        RULE_SLUGS.MATCH_PRODUCTS_KEYWORDS,
        rules
      );
    },
    [RULE_SLUGS.MATCH_RECEIPT_PRODUCTS_POINTS]: (rules) => {
      addKeywordRuleStrategy(
        activityPayload,
        RULE_SLUGS.MATCH_RECEIPT_PRODUCTS,
        rules
      );
    },
  };

  CUSTOM_HANDLED_RULE_SLUGS.forEach((slug) => {
    const matchingRules = activityPayload.activityRules.filter(
      (rule) => rule.ruleDefinitionSlug === slug
    );

    if (matchingRules.length > 0 && ruleStrategies[slug]) {
      ruleStrategies[slug](matchingRules);
    }
  });
};

const resolveActivityData = (activityData, enableActivityReordering) => {
  if (!activityData) return {};
  if (enableActivityReordering === true) return activityData;
  if (!("sequence_order" in activityData)) return activityData;
  return { ...activityData, sequence_order: null };
};

export const mapCampaignActivities = (
  activities,
  campaignOutcomes,
  params,
  campaignObject,
  enableWinningLimitExtraction = false,
  enableActivityReordering = undefined
) =>
  activities.map((activity) => {
    const {
      activityDefinition,
      activityRules,
      activityData,
      outcomes,
      id,
      name,
      description,
    } = activity;

    const activityPayload = {
      activityDefinitionSlug: activityDefinition.slug,
      name,
      description,
      activityRules: enableWinningLimitExtraction
        ? mapActivityRules(activityRules)
        : activityRules
            .filter((rule) => !IGNORED_RULE_SLUGS.includes(rule.ruleDefinition.slug))
            .map(({ ruleDefinition, ruleData }) => ({
              ruleDefinitionSlug: ruleDefinition.slug,
              ruleData,
            })),
      activityData: resolveActivityData(activityData, enableActivityReordering),
      outcomes: mapOutcomes(outcomes, campaignObject, params),
    };

    // Extract and add campaignWinningLimit only if feature flag is enabled
    if (enableWinningLimitExtraction) {
      const campaignWinningLimit = extractCampaignWinningLimit(activityRules);
      if (campaignWinningLimit) {
        activityPayload.campaignWinningLimit = campaignWinningLimit;
      }
    }

    applyCustomRuleStrategies(activityPayload);

    //sort the rules after custom handling to ensure proper order
    activityPayload.activityRules = sortActivityRules(
      activityPayload.activityRules,
      activityDefinition
    );

    const hasActivitiesConfigurationOutcomes = campaignOutcomes.some((outcome) =>
      ACTIVITIES_CONFIGURATION_OUTCOMES.includes(
        outcome.slug || outcome.outcomeDefinition?.slug
      )
    );
    if ((params.campaignId || hasActivitiesConfigurationOutcomes) && id) {
      activityPayload.id = id;
    }

    return activityPayload;
  });

export const updateActivitiesConfigurationOutcomes = (loaddata, originalData) => {
  const hasActivitiesConfigurationOutcomes = loaddata.outcomes.some((outcome) =>
    ACTIVITIES_CONFIGURATION_OUTCOMES.includes(outcome.outcomeDefinition?.slug)
  );

  if (hasActivitiesConfigurationOutcomes) {
    const activityIdMap = {};
    loaddata.activities.forEach((newActivity, index) => {
      const originalActivity = originalData.activities[index];
      if (originalActivity && originalActivity.id) {
        activityIdMap[originalActivity.id] = newActivity.id;
      }
    });

    loaddata.outcomes = loaddata.outcomes.map((outcome) => {
      if (
        ACTIVITIES_CONFIGURATION_OUTCOMES.includes(
          outcome.outcomeDefinition?.slug
        ) &&
        Array.isArray(outcome.outcomeData?.activities_configuration)
      ) {
        const updatedActivitiesConfiguration =
          outcome.outcomeData.activities_configuration.map((activityConfig) => {
            if (activityConfig.activity_id) {
              const newActivityId = activityIdMap[activityConfig.activity_id];
              if (newActivityId) {
                return {
                  ...activityConfig,
                  activity_id: newActivityId,
                };
              }
            }
            return activityConfig;
          });

        return {
          ...outcome,
          outcomeData: {
            ...outcome.outcomeData,
            activities_configuration: updatedActivitiesConfiguration,
          },
        };
      }
      return outcome;
    });
  }
};

export const getActivityName = (slug) => {
  const activities = [
    { slug: "birthday", displayText: "Birthday" },
    { slug: "bonus", displayText: "Bonus" },
    { slug: "check-in", displayText: "Check In" },
    { slug: "claim-reward", displayText: "Claim Reward" },
    { slug: "click-link", displayText: "Click Link" },
    { slug: "code-scan", displayText: "Code Scan" },
    { slug: "code-scan-generic", displayText: "Code Scan Generic" },
    { slug: "complete-profile", displayText: "Complete Profile" },
    { slug: "deeplink-action", displayText: "Deeplink Action" },
    { slug: "demo-purchase", displayText: "Demo Purchase" },
    { slug: "generic", displayText: "Generic" },
    { slug: "gift-processing", displayText: "Gift Processing" },
    { slug: "login", displayText: "Login" },
    { slug: "notifications-opt-in", displayText: "Notifications Opt In" },
    { slug: "open-app", displayText: "Open App" },
    { slug: "package-scan", displayText: "Package Scan" },
    { slug: "profile-completion", displayText: "Profile Completion" },
    { slug: "read-article", displayText: "Read an Article" },
    { slug: "receipt-scan", displayText: "Receipt Scan" },
    { slug: "refer-a-friend", displayText: "Refer a Friend" },
    { slug: "registration", displayText: "Registration" },
    { slug: "share-location", displayText: "Share Location" },
    { slug: "spin-the-wheel", displayText: "Spin the Wheel" },
    { slug: "streak", displayText: "Streak" },
    { slug: "support", displayText: "Support" },
    { slug: "survey", displayText: "Take a Survey" },
    { slug: "sweepstakes", displayText: "Sweepstakes" },
    { slug: "watch-video", displayText: "Watch Video" },
    { slug: "watch-a-video", displayText: "Watch Video" },
    { slug: "code-scan-generic", displayText: "Code Scan Generic" },
    {
      slug: "receipt-scan-complete-points",
      displayText: "Scan a Receipt Complete Points",
    },
    { slug: "code-scan-upload-points", displayText: "Scan a Code (Pre Generated)" },
    { slug: "watch-a-video", displayText: "Watch a Video" },
    { slug: "take-a-survey-mx", displayText: "Take a Survey" },
    { slug: "package-scan-points", displayText: "Scan a Package" },
    {
      slug: "package-scan-no-unique-code-points",
      displayText: "Scan a Package Without Unique Code",
    },
    { slug: "registration-generic", displayText: "Registration Generic" },
    { slug: "image-submission", displayText: "Submit Image" },
    { slug: "code-scan-external", displayText: "Code Scan External)" },
    { slug: "code-scan-universal", displayText: "Code Scan Universal" },
  ];

  return activities.find((activity) => activity.slug === slug)?.displayText;
};

/**
 * Checks if campaign steps have activities with category-based rules enabled
 * @param {Object} currentCampaignTypeConfig - The campaign type configuration object
 * @returns {boolean} - True if any activity step has isCategoryBasedRule property
 */
export const hasStepsWithCategoryBasedRules = (currentCampaignTypeConfig) => {
  if (!currentCampaignTypeConfig?.steps) {
    return false;
  }

  const activitiesSteps = currentCampaignTypeConfig.steps.filter(
    (step) =>
      step.slug === "activity-level-setup" ||
      step.slug === "activity-level-setup-new"
  );

  return activitiesSteps.some((step) => {
    if (step.values && Array.isArray(step.values)) {
      return step.values.some(
        (value) => value.type === "activities" && value.isCategoryBasedRule !== false
      );
    }
    return false;
  });
};

const reconstructSurveyConditionDisplay = (rule) => {
  const { survey_configuration, answer_options = [] } = rule.ruleData;

  const flattenedOptions = [];
  answer_options.forEach((item) => {
    if (typeof item === "string") {
      flattenedOptions.push(item);
    } else if (item && typeof item === "object" && Array.isArray(item.options)) {
      flattenedOptions.push(...item.options);
    }
  });

  const answerOptionsSet = new Set(flattenedOptions);

  const selections = survey_configuration.questions
    .map((question) => {
      const selectedOptions = question.answer_options.filter((opt) =>
        answerOptionsSet.has(opt.option_id)
      );
      if (selectedOptions.length === 0) return null;
      return {
        question_id: question.question_id,
        question_title: question.question_title,
        selected_option_ids: selectedOptions.map((opt) => opt.option_id),
        selected_option_texts: selectedOptions.map((opt) => opt.option_text),
      };
    })
    .filter(Boolean);

  const survey = {
    surveyId: survey_configuration.survey_id,
    surveyName: survey_configuration.survey_name,
    questions: survey_configuration.questions.map((question) => ({
      questionId: question.question_id,
      questionTitle: question.question_title,
      answerOptions: question.answer_options.map((option) => ({
        optionId: option.option_id,
        optionText: option.option_text,
      })),
    })),
  };

  return {
    ...rule,
    ruleData: {
      ...rule.ruleData,
      condition_display: {
        survey_id: survey_configuration.survey_id,
        survey_name: survey_configuration.survey_name,
        survey,
        selections,
      },
    },
  };
};

const reconstructSurveyOutcomeRules = (outcomes) =>
  outcomes.map((outcome) => {
    if (!outcome.outcomeRules) {
      return outcome;
    }

    return {
      ...outcome,
      outcomeRules: outcome.outcomeRules.map((rule) => {
        if (
          rule.ruleDefinition?.slug === "survey-answer-matching-rule" &&
          rule.ruleData?.survey_configuration
        ) {
          return reconstructSurveyConditionDisplay(rule);
        }
        return rule;
      }),
    };
  });

export const reconstructSurveyOutcomes = (campaignData) => {
  const result = { ...campaignData };

  if (result.outcomes) {
    result.outcomes = reconstructSurveyOutcomeRules(result.outcomes);
  }

  if (result.activities) {
    result.activities = result.activities.map((activity) => {
      if (!activity.outcomes) {
        return activity;
      }
      return {
        ...activity,
        outcomes: reconstructSurveyOutcomeRules(activity.outcomes),
      };
    });
  }

  return result;
};
